import React, { useState, useEffect } from "react";
import { Modal, Select, Typography, Space, Tag, message } from "antd";
import DialogFooter from "../../../components/DialogFooter";
import { useTheme } from "../../../services/useTheme";
import { MODULE_LOGOS } from "../../../constants/modules";
import { extractMsg } from "../../../utils/errorUtils";

export default function UserPriviligesDialog({ open, user, onClose, onSave }) {
  const [selectedModules, setSelectedModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const isDarkTheme = useTheme();

  const ALL_PRIVILEGES = Object.entries(MODULE_LOGOS(isDarkTheme)).map(
    ([label, logo]) => ({ label, logo }),
  );

  const MODULE_DEPENDENCIES = {
    Skeyecube: ["Hadoop", "Spark", "Hive"],
    Skeyepredict: ["Hadoop"],
    Jeyeda: ["Hadoop", "Skeyeingest"],
    Hive: ["Hadoop"],
  };

  useEffect(() => {
    if (open && user) {
      setSelectedModules(
        Array.isArray(user.activatedModules)
          ? user.activatedModules.map((m) => m.label)
          : [],
      );
    }
  }, [open, user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(selectedModules);
      onClose();
    } catch (e) {
      messageApi.error(extractMsg(e, "Failed to update privileges"));
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (newValues) => {
    let finalValues = newValues ? [...newValues] : [];
    const removed = selectedModules.filter((m) => !finalValues.includes(m));
    const added = finalValues.filter((m) => !selectedModules.includes(m));
    let currentFinal = new Set(finalValues);

    if (removed.length > 0) {
      let changed = true;
      while (changed) {
        changed = false;
        for (const [child, parents] of Object.entries(MODULE_DEPENDENCIES)) {
          if (currentFinal.has(child)) {
            const missingParent = parents.find((p) => !currentFinal.has(p));
            if (missingParent) {
              currentFinal.delete(child);
              changed = true;
            }
          }
        }
      }
    }

    if (added.length > 0) {
      let changed = true;
      const rejected = new Set();
      while (changed) {
        changed = false;
        for (const item of Array.from(currentFinal)) {
          if (MODULE_DEPENDENCIES[item]) {
            const missing = MODULE_DEPENDENCIES[item].filter(
              (p) => !currentFinal.has(p),
            );
            if (missing.length) {
              currentFinal.delete(item);
              if (added.includes(item))
                rejected.add(`${item} (requires: ${missing.join(", ")})`);
              changed = true;
            }
          }
        }
      }
      if (rejected.size)
        messageApi.error(
          `Cannot add modules. Missing dependencies: ${Array.from(rejected).join(" | ")}`,
        );
    }

    setSelectedModules(Array.from(currentFinal));
  };

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const privilege = ALL_PRIVILEGES.find((p) => p.label === value);
    return (
      <Tag
        color="#f0f0f0"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        closable={closable}
        onClose={onClose}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "4px 8px",
          color: "#000",
          fontSize: "14px",
          marginRight: 6,
        }}
      >
        {privilege?.logo && (
          <img
            src={privilege.logo}
            alt={label}
            style={{ width: 24, height: 24, objectFit: "contain" }}
          />
        )}
        {label}
      </Tag>
    );
  };

  return (
    <Modal
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Manage Access: {user?.username}
        </Typography.Title>
      }
      open={open}
      onCancel={onClose}
      width={700}
      footer={
        <DialogFooter
          onCancel={onClose}
          onSave={handleSave}
          loading={loading}
          saveText="Save Privileges"
        />
      }
      destroyOnHidden
    >
      {contextHolder}
      <div style={{ padding: "20px 0", minHeight: "350px" }}>
        <Typography.Paragraph>
          Select the modules this user is allowed to access.
        </Typography.Paragraph>
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder="Select modules"
          value={selectedModules}
          onChange={handleChange}
          optionLabelProp="label"
          maxTagCount={3}
          tagRender={tagRender}
        >
          {ALL_PRIVILEGES.map((p) => (
            <Select.Option key={p.label} value={p.label} label={p.label}>
              <Space align="center" size="middle">
                <img
                  src={p.logo}
                  alt={p.label}
                  style={{ width: 32, height: 32, objectFit: "contain" }}
                />
                <span style={{ fontSize: 16 }}>{p.label}</span>
              </Space>
            </Select.Option>
          ))}
        </Select>
      </div>
    </Modal>
  );
}
